/**
 * 출정 한 번의 흐름.
 *
 *   편성 → 전투 → 전진할지 철수할지 → 전투 → ...
 *
 * 전과는 철수해서 살아 돌아와야 확정된다. 그러지 않으면 막바지에 병력을
 * 전부 갈아넣어 최대한 죽이는 것이 최적 전략이 되고, 병사 영구 사망의
 * 무게가 통째로 사라진다.
 */
import {
  CANYON_SQUADS,
  Trait,
  battleSeed,
  hasTrait,
  isRouteDepth,
  routeSegment,
  sanitizeRoute,
  traitMeritPct,
  traitsForDepth,
} from '../sim/traits.ts';
import type { Route } from '../sim/traits.ts';
import { FIELD_COLS, FIELD_ROWS, RAID_ROWS, battleSupplies, battleTraits } from '../sim/scenario.ts';
import { SCOUT_AHEAD, Supply, sanitizeSupplies } from '../sim/supplies.ts';
import { Team } from '../sim/units.ts';
import {
  ELITE,
  MAX_DEPTH,
  isGate,
  Role,
  SOLDIER_LEVEL_ATK_PERMILLE,
  SOLDIER_LEVEL_HP_PERMILLE,
  companionAtDepth,
  growthRoll,
  levelXp,
} from '../sim/troops.ts';
import type { Culture } from '../sim/troops.ts';
import { RAID_BANDS, raidBand } from '../sim/troops.ts';
import type { World } from '../sim/world.ts';
import { FATIGUE_MAX } from '../sim/world.ts';
import type { RosterEntry, ScenarioConfig } from '../sim/scenario.ts';
import { Rng } from '../sim/rng.ts';
import { DROP_ROLLS, rollDrop, rollWipeLoss } from '../sim/equipment.ts';
import type { Loadout, Slot } from '../sim/equipment.ts';
import { HERO, addToStash, loadoutOf, wearDown } from './stash.ts';
import { MAX_SQUADS } from '../sim/commands.ts';
import { autoDeploy, isDeploySlot, sanitizeDeploy } from '../sim/deploy.ts';
import { skillForBattle } from './skill.ts';
import { meritScore, survivalPct } from '../game/score.ts';
import {
  COMPANION_REST,
  RAID_XP_CAP,
  battleGold,
  battleXp,
  heroDeedXp,
  heroLevelFor,
  heroLevelMerit,
  heroXpOf,
  soldierDeedXp,
  companionLevelOf,
  heroLevelOf,
  joinCompanion,
  levelsGained,
  salaryAt,
  settleRunSeed,
  soldierById,
  soldierLevel,
} from './army.ts';
import type { Army, CompanionState, Soldier } from './army.ts';
import { FRONT_REACH, RANKS, RANK_SALARY, SALARY_DEPTH, frontFloor, rankFor, salaryDepthFor, salaryFor } from './army.ts';
import { isElite, realmOf } from './army.ts';
import { TERRITORY_BY_ID, attackableBy, garrisonDepth } from './realm.ts';
import { pushLog, realmTick } from './campaign.ts';
import { captureGift } from './specialty.ts';

/** 출정 화면에서 고른 것. */
export interface Selection {
  /** 분대별 병사 uid 목록. 0번이 장수 직할대다. */
  readonly squads: readonly (readonly number[])[];
  /** 분대별 동료 식별자. 0번은 장수가 맡으므로 비어 있다. */
  readonly companions: readonly (string | null)[];
  /**
   * 장수 곁(내 부대)에서 싸우는 동료, 부관(`Plan.escort`). 편성 화면에서는 부대 자리를 차지하지 않는다 — 출정할 때
   * `startRun`이 빈 부대 자리 하나에 싣는다. 예전 형식(제 분대 자리에도 있다)도 그대로 받는다.
   */
  readonly escort?: string | null;
}

export interface RunState {
  seed: number;
  depth: number;
  /**
   * 도적 토벌인가. 장수 혼자 나가는 한 판이다.
   *
   * 부대를 전부 잃으면 돈이 나올 데가 없다. 돈은 전투에서만 나오는데 전투에는
   * 병사가 필요했다. 토벌은 병사 없이 장수만으로 성립하는 유일한 싸움이고,
   * **전과를 남기지 않는다.** 랭킹은 부대를 걸고 깊이 들어간 대가여야 한다.
   * 안전한 싸움을 반복해 순위를 올릴 수 있으면 전제가 무너진다.
   */
  raid: boolean;
  /** 토벌에서 고른 도적 무리(`RAID_BANDS`). 없으면 가장 작은 무리다. */
  raidBand?: number;
  /** 아직 확정되지 않은 누적 전과. 철수해야 계정에 들어간다. */
  pendingMerit: number;
  /** 아직 확정되지 않은 장수의 전투 성과 경험치(`heroDeedXp`). 전과와 같이 철수해야 들어오고 전멸하면 없다. */
  pendingDeedXp: number;
  /** 동료마다 아직 확정되지 않은 전투 성과 경험치. 철수 때 받을 동료(`earningCompanions`)만 받는다. */
  pendingMateDeedXp: Record<string, number>;
  /**
   * 아직 확정되지 않은 노획(냥). **출정의 노획은 전과처럼 철수해야 들어오고, 장수가 쓰러지면 모두 사라진다.**
   *
   * 처음에는 판마다 노획을 바로 계정에 넣었다 — 쓰러진 그 판의 노획만 0이었다. 계정 90개의 출정 기록을 보니 장수가
   * 쓰러진 출정도 손익이 철수한 출정의 85~95%였고 거의 모두 흑자였다(고구려 202번 가운데 195번). 기획서에는 "출정 실패:
   * 전과 0 · 노획 0"이라 적혀 있었다. 사용자와 정했다(실패의 무게): 노획도 철수할 때 확정한다 — "더 들어갈까 빠질까"의
   * 무게가 돈에도 실린다. 토벌과 영토 공격은 한 판짜리라 예전처럼 바로 들어온다. 옛 저장의 출정에는 없다(0으로 읽는다).
   */
  pendingGold?: number;
  /** 이번 출정에 데려간 총 인원. 생환 배율의 분모다. */
  deployed: number;
  /** 분대별 생존 병사. 전투마다 줄어든다. */
  squads: number[][];
  companions: (string | null)[];
  /** 장수 곁에서 싸우는 동료(`Selection.escort`). 쓰러지면 `companions`에서 빠지고 여기는 뜻이 없어진다. */
  escort: string | null;
  /**
   * 이번 출정에서 한 판이라도 전장에 서서 살아 남은 동료. 철수 때 경험치는 이들 가운데 아직 `companions`에
   * 남은 이만 받는다(`earningCompanions`). 처음에는 `companions`만 보고 나눴는데, 그때는 `createBattle`이 병사
   * 없는 분대의 동료를 세우지 않아, 빈 분대에 앉힌 동료가 전장에 서지 않고도 받아 갔다 — 위험 없는 육성법이
   * 됐다(리뷰가 짚었다). 지금은 빈 분대의 동료도 혼자 서지만(단신 출전), 명단이 아니라 전장을 기준으로 세는
   * 규칙은 남긴다 — 경험치는 싸운 사람의 것이다.
   */
  stood: string[];
  battles: number;
  over: boolean;
  /** 장수가 쓰러졌는가. 그러면 전과는 0이다. */
  failed: boolean;
  /**
   * 좁은 협곡(`Trait.Canyon`)에서 이 깊이에 데려갈 분대(`CANYON_SQUADS`까지). 판을 시작하기 전에 고른다(`needsPick`,
   * `pickSquads`). 나머지 분대는 이 판만 쉬고 출정에는 남는다 — 죽지도, 경험치를 받지도 않는다. 깊이를 적어 두어 다음
   * 협곡에서 지난 고르기를 쓰지 않는다. 출정(`RunState`)은 계정에 적히지 않는다 — 판 도중에 새로 고치면 출정을
   * 버리는 것이라 고르기도 함께 사라진다(처음 주석은 "새로 고쳐도 같은 분대"라고 잘못 적었다 — 리뷰가 짚었다).
   */
  fielded?: { depth: number; squads: number[] };
  /**
   * 갈림길에서 고른 길(`Route`)과 그 구간(`routeSegment`). 관문을 넘어 새 구간에 들면 다시 고른다(`needsRoute`) —
   * 구간을 적어 두어 지난 구간의 길을 새 구간에 쓰지 않는다.
   */
  route?: { segment: number; id: Route };
  /** 척후(군수품)가 안개 없이 본 가장 깊은 곳(`scouted`). 척후를 쓴 판을 정산할 때 적는다(`SCOUT_AHEAD`). */
  scoutTo?: number;
  /**
   * 영토 공격(Todo9 4단계)이면 그 땅과 떠날 때의 주인. 한 판으로 끝나고, 적은 그 땅의 수비대(주인의 병종, 세기는
   * `garrisonDepth`), 지형은 그 땅의 것이다. 이기면 땅을 빼앗는다(`applyBattle`). 깊이 기록·지름길·동료 합류·최고 전과는
   * 출정의 몫이라 남기지 않는다 — 전과는 관등·성장에만 든다(`retreat`).
   */
  territory?: { id: string; owner: Culture };
  /** 이 출정에 먼저 낸 급료(`wageFor`, Todo6). 결과 화면이 번 돈과 견줘 순이익을 적는다. 토벌은 0. */
  wage?: number;
  /**
   * 병사마다 쌓인 피로(uid → 0~`FATIGUE_MAX`, Todo6). 판을 정산할 때 살아 돌아온 병사의 값을 적고(`applyBattle`), 다음
   * 판의 명부에 싣는다(`buildConfig`). 출정 안에서 깊이를 넘어 이어지고, 철수하면 출정과 함께 사라진다.
   */
  fatigue?: Record<number, number>;
  /**
   * 이 깊이에 쓸 군수품(supplies.ts, 세 가지까지). 판을 시작하기 전(전투 준비)에 고른다(`needsSupplyChoice`,
   * `chooseSupplies`). 고르지 않기로 한 것도 적는다(빈 목록) — 같은 판에서 다시 묻지 않게. 쓴 것은 그 판을 정산할 때
   * 계정에서 하나씩 준다(`applyBattle`).
   */
  supplyUse?: { depth: number; ids: Supply[] };
  /**
   * 출전 위치를 고른 깊이(Todo10, `chooseDeploy`). 이 깊이에서 고른 뒤에는 다시 묻지 않는다 — 처음에는 표시가 없어
   * "나선다"를 누르면 준비 화면이 다시 떠 판에 들어가지 못했다. 고른 칸 자체는 계정(`Army.deploy`)에 남는다.
   */
  deployChosen?: number;
  /**
   * 이번 출정에서 얻고 잃은 것의 합. 결과 화면이 "이번 출정 누계"로 보인다 — 한 판의 결과만 보이면
   * 열 판째에 지금까지 무엇을 벌었는지 알 길이 없었다(플레이한 사람이 청했다). 전과와 달리 돈·물건·사람은
   * 얻는 판에 이미 계정에 들어갔으므로 여기는 세기만 한다. 장수가 쓰러져도 남는다.
   */
  tally: RunTally;
  /**
   * 이 출정에서 **이긴** 가장 깊은 깊이(출정만 — 토벌·영토 공격은 적지 않는다). 한 판도 못 이겼으면 없다. 월급의 자격을
   * 가른다(`salaryDue`).
   */
  wonDepth?: number;
}

/**
 * **부상**(밸런스 2). 이긴 판에서 쓰러진 병사 가운데 이만큼(백분율)은 죽지 않고 다쳐서 병영으로 돌아간다 — 그 출정은
 * 쉬고(출정 명단에서 빠진다), 이 판의 경험치는 없다. 진 판(장수가 쓰러졌거나 적을 다 쓸지 못한 판)에서 쓰러지면 지금처럼
 * 죽는다. 토벌에는 병사가 없다.
 *
 * 까닭: 기준 플레이어의 계정을 되풀이해 재니(`npm run career`) 병사가 자라지 못했다. 철수한 출정에서도 넷에 하나를 잃고
 * 민병으로 채워, 장수가 레벨 33이 되도록 병사는 평균 2.5티어였다 — "살아남은 병사가 큰다"가 이 플레이어에게는 없는
 * 일이었다. 사용자가 골랐다(2026-10-02): 부상 30% + 병사 경험치 두 배 + 적 곡선 1.17배. 셋을 함께 넣으면 관문까지의
 * 출정 수가 7·4·11·18.5번에서 3·3·5·5번(기준 2~3·3~4·4~5·5~6)이 된다. 영구 사망은 남는다 — 열에 일곱은 여전히 죽는다.
 *
 * 누가 다쳤는지는 판의 시드와 병사 uid가 정한다(`woundedRoll`) — 난수를 굴리는 순서에 기대지 않고, 서버가 같은 답을 낸다.
 */
export const WOUNDED_PCT = 30;

/**
 * 이긴 판에서 쓰러진 이 병사가 부상으로 살아 돌아오는가. `seed`는 정산 시드(`settleSeed`). `pct`: 용사는 `ELITE.woundedPct`.
 * 같은 굴림을 문턱만 달리 본다 — 여느 병사로 돌아올 병사는 용사여도 돌아온다.
 */
export function woundedRoll(seed: number, uid: number, pct: number = WOUNDED_PCT): boolean {
  return (Math.imul((uid ^ seed) | 0, 0x9e3779b1) >>> 0) % 100 < pct;
}

export interface RunTally {
  /** 이긴 판에서 쓰러졌다가 부상으로 병영에 돌아간 병사 수(`WOUNDED_PCT`). 잃은 병사(`lost`)에는 들지 않는다. */
  hurt: number;
  gold: number;
  /** 주운 장비 id, 주운 순서. */
  loot: string[];
  /** 합류한 동료 id. */
  joined: string[];
  /** 쓰러지거나 흩어져 잃은 병사 수. */
  lost: number;
}

/**
 * 출정을 시작한다. `depth`는 출발 깊이(지름길, `startDepths`) — 토벌은 늘 1이다. 여기서는 범위 밖 값(정수가
 * 아니거나 1~`MAX_DEPTH` 밖)만 1로 접는다. 그 계정에 **열린** 깊이인지는 화면(`startDepths`)과 서버가 본다 —
 * 계정 없이 부르는 곳(시험·도구)이 있어 여기서는 가릴 수 없다.
 */
export function startRun(selection: Selection, seed: number, raid = false, depth = 1, band = 0): RunState {
  const squads = selection.squads.map((list) => [...list]);
  const deployed = squads.reduce((n, list) => n + list.length, 0);
  // 부관을 명부에 싣는다: 출정 명부는 분대별 동료라(`companions`), 병사도 지휘관도 없는 부대 자리 하나에 앉힌다. 전장에서는
  // 그 자리가 아니라 0번 분대에 선다(`escortSquad`). 경험치·소실은 이 자리로 센다. 자리가 없으면 부관 없이 나간다.
  const companions = [...selection.companions];
  let escort: string | null = null;
  const esc = selection.escort;
  if (esc && !companions.includes(esc)) {
    const seat = companions.findIndex((c, sq) => sq > 0 && c === null && (squads[sq]?.length ?? 0) === 0);
    if (seat > 0) {
      companions[seat] = esc;
      escort = esc;
    }
  } else if (esc) {
    escort = esc;
  }
  return {
    seed,
    depth: !raid && Number.isInteger(depth) && depth >= 1 && depth <= MAX_DEPTH ? depth : 1,
    raid,
    ...(raid && raidBand(band) > 0 ? { raidBand: raidBand(band) } : {}),
    pendingMerit: 0,
    pendingDeedXp: 0,
    pendingMateDeedXp: {},
    deployed,
    squads,
    companions,
    escort,
    stood: [],
    battles: 0,
    over: false,
    failed: false,
    tally: { hurt: 0, gold: 0, loot: [], joined: [], lost: 0 },
  };
}

/**
 * 영토를 치러 나선다(Todo9 4단계). 출정과 같은 편성(`Selection`)으로, 깊이는 그 땅의 수비 세기다. 칠 수 없는 땅(수도,
 * 맞닿지 않은 땅, 제 땅)이면 null — 화면이 막지만 여기서도 거른다.
 */
export function startTerritoryRun(army: Army, selection: Selection, seed: number, id: string): RunState | null {
  const realm = realmOf(army);
  const t = TERRITORY_BY_ID.get(id);
  if (!t || !attackableBy(realm, army.culture).some((x) => x.id === id)) return null;
  const run = startRun(selection, seed, false, garrisonDepth(id));
  run.territory = { id, owner: realm.owner[id] };
  return run;
}

/** 지금 남은 병력으로 다음 전투의 설정을 만든다. */
export function buildConfig(army: Army, run: RunState): ScenarioConfig {
  const roster: RosterEntry[] = [];
  for (let sq = 0; sq < run.squads.length; sq++) {
    // 같은 병종·같은 레벨끼리 묶어야 스폰 순서가 안정적이다. 레벨이 다르면 몸이
    // 다르므로 줄을 나눈다 — 한 줄에 섞으면 명부가 한 레벨밖에 싣지 못한다.
    const byKind = new Map<string, { troop: string; level: number; uids: number[]; elite: boolean }>();
    for (const uid of run.squads[sq]) {
      const s = soldierById(army, uid);
      if (!s) continue;
      const level = soldierLevel(s);
      const elite = isElite(s);
      // 용사가 지킬 사람은 싣지 않는다 — 제가 든 부대(줄의 `squad`)의 대장이다(`World.bindDuties`).
      const key = `${s.troop}:${level}:${elite ? 1 : 0}`;
      const row = byKind.get(key);
      if (row) row.uids.push(uid);
      else byKind.set(key, { troop: s.troop, level, uids: [uid], elite });
    }
    for (const row of byKind.values()) {
      // 앞선 판의 피로(`RunState.fatigue`). 모두 0이면 싣지 않는다 — 첫 판의 명부는 예전과 같다.
      const fat = row.uids.map((u) => run.fatigue?.[u] ?? 0);
      roster.push({
        troop: row.troop,
        count: row.uids.length,
        squad: sq,
        uids: row.uids,
        level: row.level,
        ...(fat.some((f) => f > 0) ? { fatigue: fat } : {}),
        ...(row.elite ? { elite: true as const } : {}),
      });
    }
  }

  // 영토 공격은 그 땅의 주인과 지형으로 싸운다(`RunState.territory`). 갈림길은 없다.
  const enemy = run.territory ? run.territory.owner : enemyCultureFor(army.culture, run.depth);
  const route = run.raid || run.territory ? null : routeOf(run, run.depth);
  const traits = runTraits(run, run.depth);
  // 좁은 협곡: 고른 분대만 싣는다. 나머지 분대의 병사와 동료는 이 판만 쉰다. 부관은 장수와 함께 선다.
  const escortSquad = run.escort ? run.companions.indexOf(run.escort) : -1;
  const picked = hasTrait(traits, Trait.Canyon) ? pickedSquads(run) : null;
  const sits = (sq: number): boolean => picked !== null && sq !== escortSquad && !picked.includes(sq);
  return {
    raid: run.raid,
    // 고른 도적 무리. 가장 작은 무리는 싣지 않는다 — 예전의 명부와 같은 꼴이다.
    ...(run.raid && raidBand(run.raidBand) > 0 ? { raidBand: raidBand(run.raidBand) } : {}),
    seed: battleSeed(run.seed, run.depth),
    // 전장 특성. 출정 시드와 깊이(갈림길 구간이면 고른 길)가 정한다(`traitsForDepth`) — 결과 화면의 예보와 같은 값이다.
    // 토벌에는 없다.
    traits,
    ...(route ? { route } : {}),
    cols: FIELD_COLS,
    rows: run.raid ? RAID_ROWS : FIELD_ROWS,
    depth: run.depth,
    playerCulture: army.culture,
    enemyCulture: enemy,
    roster: picked ? roster.filter((e) => !sits(e.squad ?? 0)) : roster,
    // 분대 색인을 그대로 넘긴다. 걸러내면 남은 동료가 다른 분대로 옮겨간다. 쉬는 분대의 동료는 비운다.
    companions: run.companions.map((c, sq) => (sits(sq) ? null : c)),
    // 이 전투가 시작하는 시점의 착장을 떠서 싣는다. 계정 상태를 그대로 넘기면
    // 전투가 도는 중에 장비를 바꿨을 때 이미 시작한 전투의 규칙이 달라진다.
    // 출정 전체가 아니라 전투마다 다시 뜬다. 깊이 사이에서는 바꿀 수 있다.
    loadouts: snapshotLoadouts(army, run),
    // 동료 레벨도 착장과 같이 이 전투 시작 시점의 값을 뜬다. 전투 판정에 들어가는
    // 값이라 계정을 그대로 가리키면 서버가 같은 전투를 다시 못 만든다.
    companionLevels: run.companions.map((id) => companionLevelOf(army, id)),
    // 장수 곁에서 싸우는 동료의 제 분대 자리. 쓰러져 명단에서 빠졌으면 싣지 않는다.
    escortSquad,
    // 이 판에 쓰는 군수품. 고른 뒤 다 써서 없는 것은 싣지 않는다(`usableSupplies`).
    supplies: run.raid ? [] : usableSupplies(army, run),
    // 출전 위치(Todo10). 토벌은 장수 혼자라 싣지 않는다 — 싣지 않으면 옛 방식(한 덩어리에 섞여 선다)이다.
    ...(run.raid ? {} : { deploy: deployLayout(army, run) }),
    // 장수 기술(Todo10). 토벌에도 장수가 들고 나간다.
    skill: skillForBattle(army),
    // 출정 맥락: 생환 배율의 분모(출정에 처음 데려간 수)와 이 판을 쉰 병사. 서버(`summarize`)가 정산(`applyBattle`)과
    // 같은 전과를 다시 낸다.
    runDeployed: run.deployed,
    resting: picked ? roster.filter((e) => sits(e.squad ?? 0)).reduce((n, e) => n + e.count, 0) : 0,
    // 장수 레벨도 같은 까닭으로 뜬다. 출정 중에는 전과가 확정되지 않으니 바뀌지 않지만,
    // 계정을 가리키지 않고 값을 싣는 것이 서버가 같은 전투를 만드는 길이다.
    heroLevel: heroLevelOf(army),
  };
}

/**
 * 좁은 협곡에서 싸울 수 있는(데려갈 수 있는) 분대: 병사가 있거나 동료가 맡은 분대. 부관 자리는 빼고 센다 — 부관은
 * 장수와 함께 선다.
 */
export function squadsAvailable(run: RunState): number[] {
  const escortSquad = run.escort ? run.companions.indexOf(run.escort) : -1;
  const out: number[] = [];
  for (let sq = 0; sq < MAX_SQUADS; sq++) {
    if (sq === escortSquad) continue;
    if ((run.squads[sq]?.length ?? 0) > 0 || run.companions[sq]) out.push(sq);
  }
  return out;
}

/** 이 판을 시작하기 전에 데려갈 분대를 골라야 하는가 — 좁은 협곡이고, 싸울 분대가 상한보다 많고, 아직 안 골랐다. */
export function needsPick(run: RunState): boolean {
  if (run.raid || !hasTrait(runTraits(run, run.depth), Trait.Canyon)) return false;
  if (squadsAvailable(run).length <= CANYON_SQUADS) return false;
  return run.fielded?.depth !== run.depth;
}

/**
 * 병사가 한 명이라도 서는 고르기인가. 동료만 있는 분대끼리 고르면 명부가 비어 첫 틱에 "병력이 전부 쓰러졌다"로
 * 끝나고, 서버도 "출전 명부가 비어 있습니다"로 거절한다(리뷰가 짚었다). 화면이 이것으로 확정을 막는다.
 */
export function pickHasSoldiers(run: RunState, squads: readonly number[]): boolean {
  return squads.some((sq) => (run.squads[sq]?.length ?? 0) > 0);
}

/**
 * 처음 골라 둘 분대: 병사가 있는 분대부터 오름차순으로, 모자라면 동료만 있는 분대로 채운다. 앞의 둘만 보면 동료만
 * 있는 분대 둘이 골라져 그대로 나갈 수 없는 기본값이 됐다.
 */
export function defaultPick(run: RunState): number[] {
  const all = squadsAvailable(run);
  const manned = all.filter((sq) => (run.squads[sq]?.length ?? 0) > 0);
  return [...manned, ...all.filter((sq) => !manned.includes(sq))].slice(0, CANYON_SQUADS).sort((a, b) => a - b);
}

/**
 * 데려갈 분대를 적는다. 싸울 수 있는 분대만, `CANYON_SQUADS`까지, 오름차순. 병사가 하나도 없는 고르기는 받지 않고
 * 기본값(`defaultPick`)을 적는다 — 화면이 막지만 이 문을 거치는 다른 길도 같은 규칙을 지키게.
 */
export function pickSquads(run: RunState, squads: readonly number[]): void {
  const ok = squadsAvailable(run);
  const list = [...new Set(squads)].filter((sq) => ok.includes(sq)).sort((a, b) => a - b).slice(0, CANYON_SQUADS);
  run.fielded = { depth: run.depth, squads: pickHasSoldiers(run, list) ? list : defaultPick(run) };
}

/** 이 깊이에 설 분대. 골랐으면 그것, 안 골랐으면(도구·시험, 고를 것이 둘 이하) 기본값(`defaultPick`). */
function pickedSquads(run: RunState): number[] {
  if (run.fielded && run.fielded.depth === run.depth && run.fielded.squads.length > 0) return run.fielded.squads;
  return defaultPick(run);
}

// ---------------------------------------------------------------------------
// 출전 위치(Todo10, `sim/deploy.ts`)
// ---------------------------------------------------------------------------

/**
 * 이 판에 제 칸으로 서는 분대: 장수 분대(0)와, 병사가 있거나 동료가 맡은 분대. 장수 곁 분대는 장수와 함께 서서 제 칸이
 * 없다(`squadsAvailable`이 뺀다). 좁은 협곡이면 데려갈 분대만 — 화면이 고르는 중이면 그 고르기(`picked`).
 */
export function deployStanding(run: RunState, picked?: readonly number[]): number[] {
  const all = squadsAvailable(run);
  const list = hasTrait(runTraits(run, run.depth), Trait.Canyon) ? (picked ?? pickedSquads(run)) : all;
  return [0, ...list.filter((sq) => sq !== 0 && all.includes(sq))].sort((a, b) => a - b);
}

/** 분대의 병종별 수(기본 배치가 주력 가지를 본다). */
function squadTroops(army: Army, run: RunState, sq: number): { troop: string; count: number }[] {
  const count = new Map<string, number>();
  for (const uid of run.squads[sq] ?? []) {
    const s = soldierById(army, uid);
    if (s) count.set(s.troop, (count.get(s.troop) ?? 0) + 1);
  }
  return [...count].map(([troop, n]) => ({ troop, count: n }));
}

/** 이 판의 출전 위치: 마지막으로 고른 칸(`Army.deploy`), 없으면 병종으로 정하는 기본 배치. 서지 않는 분대는 -1. */
export function deployLayout(army: Army, run: RunState, picked?: readonly number[]): number[] {
  const standing = deployStanding(run, picked);
  return sanitizeDeploy(army.deploy, autoDeployFor(army, run, standing));
}

/** 고른 것이 없을 때의 기본 배치(화면의 "기본 배치로"). */
export function autoDeployFor(army: Army, run: RunState, standing: readonly number[]): number[] {
  return autoDeploy((sq) => (standing.includes(sq) ? squadTroops(army, run, sq) : null));
}

/**
 * 출전 위치를 물어야 하는가 — 제 칸으로 서는 분대가 둘 이상(동료가 합류한 뒤)이고, 이 깊이에 아직 안 골랐다. 토벌은
 * 장수 혼자다.
 */
export function needsDeploy(run: RunState): boolean {
  return !run.raid && run.deployChosen !== run.depth && deployStanding(run).length >= 2;
}

/**
 * 고른 출전 위치를 계정에 적는다(다음 판이 이것으로 연다). 칸 번호가 아닌 분대는 앞서 고른 값을 둔다. 이 깊이에서는
 * 다시 묻지 않는다.
 */
export function chooseDeploy(army: Army, run: RunState, slots: readonly number[]): void {
  const prev = army.deploy ?? new Array<number>(MAX_SQUADS).fill(-1);
  army.deploy = prev.map((p, sq) => (isDeploySlot(slots[sq]) ? slots[sq] : p));
  run.deployChosen = run.depth;
}

/** 이 판을 시작하기 전에 쓸 군수품을 물어야 하는가 — 가진 것이 있고, 이 깊이에 아직 안 골랐다. 토벌에는 쓰지 않는다. */
export function needsSupplyChoice(army: Army, run: RunState): boolean {
  if (run.raid || !Object.values(army.supplies).some((n) => n > 0)) return false;
  return run.supplyUse?.depth !== run.depth;
}

/**
 * 그 깊이에 고른 길. 갈림길 구간이 아니거나, 그 구간의 길을 아직 안 골랐으면 null. 결과 화면이 다음 깊이를 예보할 때도
 * 쓴다 — 관문 다음 깊이는 아직 길을 모른다.
 */
export function routeOf(run: RunState, depth: number): Route | null {
  if (run.raid || !isRouteDepth(depth) || !run.route || run.route.segment !== routeSegment(depth)) return null;
  return run.route.id;
}

/**
 * 출정에 남은 병사의 평균 피로(백분율, 0~100, Todo6). 결과 화면이 적는다. 아직 싸우지 않은 병사는 0으로 센다. 병사가
 * 없으면 0.
 */
export function averageFatiguePct(run: RunState): number {
  const uids = run.squads.flat();
  if (uids.length === 0) return 0;
  let sum = 0;
  for (const u of uids) sum += run.fatigue?.[u] ?? 0;
  return Math.round((sum * 100) / (uids.length * FATIGUE_MAX));
}

/** 척후가 그 깊이를 안개 없이 보았는가(`RunState.scoutTo`). */
export function scouted(run: RunState, depth: number): boolean {
  return run.scoutTo !== undefined && depth <= run.scoutTo;
}

/** 그 깊이의 전장 특성(고른 길로). 길을 아직 안 골랐으면 빈 목록 — 화면은 `routeOf`로 갈림길을 따로 적는다. */
export function runTraits(run: RunState, depth: number): Trait[] {
  if (run.raid) return [];
  // 영토는 그 땅의 지형 하나다(`TerritoryDef.land`).
  if (run.territory) {
    const land = TERRITORY_BY_ID.get(run.territory.id)?.land;
    return land ? [land] : [];
  }
  return traitsForDepth(run.seed, depth, routeOf(run, depth));
}

/** 판을 시작하기 전에 갈 길을 골라야 하는가 — 갈림길 구간인데 이 구간의 길을 아직 안 골랐다. */
export function needsRoute(run: RunState): boolean {
  return !run.raid && !run.territory && isRouteDepth(run.depth) && routeOf(run, run.depth) === null;
}

/** 길을 적는다. 모르는 값이면 적지 않는다(`needsRoute`가 계속 묻는다). */
export function chooseRoute(run: RunState, id: string): void {
  const route = sanitizeRoute(id);
  if (route && isRouteDepth(run.depth)) run.route = { segment: routeSegment(run.depth), id: route };
}

/** 판을 시작하기 전에 전투 준비 화면을 띄워야 하는가 — 좁은 협곡의 분대 고르기나 군수품 고르기. */
export function needsPrep(army: Army, run: RunState): boolean {
  return needsPick(run) || needsSupplyChoice(army, run) || needsDeploy(run);
}

/** 이 판에 쓸 군수품을 적는다. 가진 것만, 겹치지 않게 세 가지까지(`sanitizeSupplies`). 빈 목록이면 쓰지 않는다. */
export function chooseSupplies(army: Army, run: RunState, ids: readonly string[]): void {
  run.supplyUse = { depth: run.depth, ids: sanitizeSupplies(ids).filter((id) => (army.supplies[id] ?? 0) > 0) };
}

/** 이 판에 실을 군수품: 이 깊이에 고른 것 가운데 아직 가진 것. */
function usableSupplies(army: Army, run: RunState): Supply[] {
  if (run.supplyUse?.depth !== run.depth) return [];
  return run.supplyUse.ids.filter((id) => (army.supplies[id] ?? 0) > 0);
}

/** 분대 색인에 맞춰 착장을 뜬다. 0번은 장수, 그 뒤는 그 분대를 맡은 동료다. */
function snapshotLoadouts(army: Army, run: RunState): (Loadout | null)[] {
  const out: (Loadout | null)[] = [{ ...loadoutOf(army, HERO) }];
  for (let sq = 1; sq < MAX_SQUADS; sq++) {
    const id = run.companions[sq];
    out.push(id ? { ...loadoutOf(army, id) } : null);
  }
  return out;
}

/** 깊이마다 상대가 바뀐다. 같은 적만 계속 만나면 편성이 굳는다. */
export function enemyCultureFor(mine: Culture, depth: number): Culture {
  const others = ([0, 1, 2] as Culture[]).filter((c) => c !== mine);
  return others[(depth - 1) % others.length];
}

/** 한 착용자가 전멸로 잃거나 깎인 것. */
export interface GearLoss {
  /** HERO 또는 동료 식별자. */
  readonly who: string;
  /** 잃은 칸과 물건(0단계였던 것). */
  readonly slots: readonly Slot[];
  readonly items: readonly string[];
  /** 잃지 않고 ★ 하나가 깎인 강화 물건(`wearDown`). 옛 결과에는 없다. */
  readonly worn?: readonly { readonly slot: Slot; readonly from: string; readonly to: string }[];
}

/**
 * 결과 화면이 부르는 병사 한 명: 이름과 함께 병종·레벨. 이름만 적으면 쓰러진 것이 막 뽑은
 * 민병인지 오래 키운 정예인지 알 수 없다(플레이한 사람이 짚었다). 레벨은 싸우러 나설 때의
 * 것이다 — 이번 판 경험치는 "레벨이 오른 병사"가 따로 보인다.
 */
export interface Who {
  readonly name: string;
  /** 병종 id(`TROOP_BY_ID`). 화면이 이름을 붙인다. */
  readonly troop: string;
  readonly level: number;
}

/** 한 병사의 이번 전투 전공. */
export interface Deed extends Who {
  /** 병사 uid. 결과 화면이 레벨이 오른 병사(`LevelUp.uid`)와 맞춘다. */
  readonly uid: number;
  /** 벤 적의 수. */
  readonly kills: number;
  /** 전투 성과로 더 받은 경험치(`soldierDeedXp`). 살아 돌아오지 못했으면 0. */
  readonly bonusXp: number;
  /** 이번 판에 오른 레벨 수(소수, `levelsGained`). 살아 돌아오지 못했으면 0. */
  readonly levelGain: number;
  /** 경험치를 받은 뒤의 레벨. `level`(나설 때)보다 크면 이번 판에 레벨이 올랐다. */
  readonly levelTo: number;
  /** 살아 돌아왔나, 쓰러졌나, 장수를 잃고 흩어졌나. */
  readonly fate: 'alive' | 'fell' | 'lost' | 'hurt';
}

export interface LevelUp {
  readonly uid: number;
  readonly name: string;
  /** 오른 뒤의 레벨. */
  readonly level: number;
  /** 이번에 오른 레벨들에서 받은 체력·공격 몫의 합(천분율 포인트 ‰, `growthRoll`). */
  readonly hp: number;
  readonly atk: number;
  /** 그 레벨 수만큼의 평균 몫. 화면이 운을 견준다. */
  readonly usualHp: number;
  readonly usualAtk: number;
}

export interface BattleOutcome {
  /** 이 판에 쓴 군수품(supplies.ts). 결과 화면이 적는다. */
  readonly usedSupplies: readonly Supply[];
  readonly won: boolean;
  readonly leaderAlive: boolean;
  readonly killScore: number;
  readonly fallen: number;
  readonly survivors: number;
  /** 이번 전투에서 쓰러져 잃은 병사, 뽑힌 순서(uid)대로. 부상으로 돌아간 병사(`woundedMen`)는 여기 없다. */
  readonly fallenMen: Who[];
  /** 이긴 판에서 쓰러졌지만 다쳐서 병영으로 돌아간 병사(`WOUNDED_PCT`). 이 출정은 쉰다. */
  readonly woundedMen: Who[];
  /**
   * 장수가 쓰러진 뒤 후퇴 시간(`FALL_RETREAT_TICKS`) 안에 빠져나가지 못해 잃은 병사. 쓰러진 병사와 한 줄로 합쳐
   * "쓰러진 병사"라고 적으면 산 사람을 죽었다고 부르게 된다(리뷰에서 짚었다). 빠져나간 병사는 살아 돌아온다.
   */
  readonly scatteredMen: Who[];
  readonly gold: number;
  readonly xpEach: number;
  /** 이번 전투로 레벨이 오른 병사, uid 순. */
  readonly levelUps: LevelUp[];
  /** 전장에 선 병사마다 전공. 많이 벤 순, 같으면 먼저 뽑힌 순. */
  readonly deeds: Deed[];
  /** 장수가 벤 적의 수. */
  readonly leaderKills: number;
  /**
   * 전장에 선 동료마다 벤 적의 수. 부관이 먼저, 그다음 분대 순. 처음에는 장수와 병사만 적어, 부관 온달이 곁에서 벤 적이
   * 어디에도 보이지 않았다(플레이한 사람: "온달이 벤 적이 안나왔다. 부관이 벤것도 표시해줘").
   */
  readonly companionDeeds: {
    /** 동료 id(`CompanionState.id`). 결과 화면이 계정의 경험치를 찾아 오를 레벨을 잰다. */
    readonly id: string;
    readonly name: string;
    readonly kills: number;
    readonly escort: boolean;
    /** 이번 판의 전투 성과 경험치(`heroDeedXp`). 쓰러졌거나 토벌이면 0. 철수해야 들어온다. */
    readonly deedXp: number;
    /** 쓰러져 요양에 들어갔나. */
    readonly downed: boolean;
  }[];
  /** 이번 판에 쌓인 전과(`meritScore`). 토벌·전멸이면 0. 장수와 함께 선 동료가 철수 때 경험치로 받는다. */
  readonly battleMerit: number;
  readonly downedCompanions: string[];
  /** 이번 전투로 합류한 동료. 없으면 null. */
  readonly joined: string | null;
  /** 이번 전투에서 주운 것. 없으면 null. */
  readonly drop: string | null;
  /** 이 판에서 주운 것 모두(`DROP_ROLLS` — 둘까지). `drop`은 그 첫째다. */
  readonly drops: readonly string[];
  /** 전멸로 사라진 장비. 전멸하지 않았으면 빈 배열이다. */
  readonly gearLost: GearLoss[];
  /** 지금 철수하면 받을 전과. */
  readonly meritIfRetreat: number;
  /** 지금 철수하면 받을 노획(쌓아 둔 것, `RunState.pendingGold`). 토벌·영토 공격은 0 — 이미 들어왔다. */
  readonly goldIfRetreat: number;
  /** 장수가 쓰러져 사라진 쌓인 노획. 쓰러지지 않았으면 0. */
  readonly goldLost: number;
  /** 토벌에서 장수가 얻은 경험치(`Army.raidXp`에 쌓인 몫). 토벌이 아니면 0. */
  readonly raidXp: number;
  /** 이번 판에서 장수가 번 전투 성과 경험치. 철수해야 들어온다. */
  readonly leaderDeedXp: number;
  /** 지금 철수하면 들어올 장수 성과 경험치(이번 출정 누계). */
  readonly deedXpIfRetreat: number;
  /** 이 깊이를 처음 이겼나(`Army.deepest`를 넘었다). 토벌은 늘 false. */
  readonly firstClear: boolean;
  /** 영토 공격에서 빼앗은 땅(`RunState.territory`). 아니면 null. */
  readonly captured: string | null;
  /** 빼앗은 판에 받은 그 땅의 특산(장비 id). 아니면 null. */
  readonly gift: string | null;
}

/**
 * 전투가 끝난 뒤의 주사위에 쓰는 시드.
 *
 * 전투 난수와 겹치면 안 된다. `World.rng`를 그대로 쓰면 전투 코드가 난수를 한 번
 * 더 쓰도록 바뀌는 것만으로 전리품이 달라지고, `new Rng(world.seed)`는 전투가 쓰는
 * 수열과 같은 자리에서 출발한다. 그래서 같은 시드에 상수를 섞어 따로 뽑는다.
 *
 * 시드를 `run`이 아니라 `world`에서 꺼내는 이유는, 서버가 가진 것이 리플레이의
 * `cfg.seed`뿐이기 때문이다. 여기서 `buildConfig`의 식을 다시 적으면 그쪽을 고쳤을
 * 때 서버가 정산 시드를 재구성하지 못하는데, 로컬에서는 아무것도 깨지지 않아
 * 눈치챌 방법이 없다.
 */
function settleSeed(world: World): number {
  return world.seed ^ 0x5e7713;
}

/**
 * 전투 결과를 계정에 반영한다.
 * 죽은 병사는 여기서 사라진다. 되돌릴 방법은 없다.
 */
export function applyBattle(
  army: Army,
  run: RunState,
  world: World,
  cfg: ScenarioConfig,
): BattleOutcome {
  const leaderAlive = world.leaderAlive();
  // 이 출정의 시드를 다 썼다 — 다음 출정은 새 시드다(`settleRunSeed`). 첫 판을 정산할 때까지 남겨 두어, 도중에 새로
  // 고쳐 출정을 버려도 같은 특성으로 다시 나선다.
  settleRunSeed(army, run.seed);
  const enemyLeft = world.aliveCount(Team.Enemy);
  const killScore = world.stats.killScore[Team.Player];

  // 전장에 나갔던 병사의 생사를 uid로 되돌려 받는다.
  const dead = new Set<number>();
  const alive = new Set<number>();
  // 빠져나간 병사. 장수가 쓰러진 판에서는 이들만 살아 돌아온다.
  const exited = new Set<number>();
  const downed: string[] = [];
  // 실제로 전장에 선 동료. 명부가 아니라 전장을 기준으로 센다 — 명부의 동료를 createBattle이 건너뛰는
  // 경우(모르는 id, 두 분대에 적힌 같은 동료)가 있다. 나가지도 않은 동료가 전멸에 장비를 잃으면 안 된다.
  // 서버도 리플레이를 돌려 같은 것을 본다.
  const standing: { squad: number; id: string }[] = [];
  const companionDeeds: { id: string; name: string; kills: number; escort: boolean; deedXp: number; downed: boolean; squad: number }[] = [];
  // 병사마다 벤 적의 수(`World.killCount`). 결과 화면의 병사별 전공이다.
  const killsOf = new Map<number, number>();
  // 이 판에 용사의 몸으로 선 병사(스펙의 `elite`). 계정이 아니라 전장을 본다 — 서버도 리플레이에서 같은 것을 본다.
  const elites = new Set<number>();
  // 병사마다 전투 성과 경험치의 바탕(친 횟수·맞은 횟수). 살아 돌아온 병사만 받는다.
  const deedOf = new Map<number, number>();
  let leaderDeedXp = 0;
  for (let i = 0; i < world.count; i++) {
    if (world.team[i] !== Team.Player) continue;
    if (world.role[i] === Role.Soldier) {
      killsOf.set(world.uid[i], world.killCount[i]);
      if (world.specs[world.specIdx[i]].elite === true) elites.add(world.uid[i]);
      deedOf.set(world.uid[i], soldierDeedXp(world.killCount[i], world.hitsDealt[i], world.hitsTaken[i], run.depth));
      // 빠져나간 병사는 산 것이다. `alive`만 보면 후퇴한 병사가 영구히 죽는다.
      if (world.alive[i] === 1 || world.exited[i] === 1) alive.add(world.uid[i]);
      else dead.add(world.uid[i]);
      // 살아 돌아온 병사의 피로를 다음 판으로(`RunState.fatigue`). 쓰러진 병사는 지운다.
      if (!run.raid) {
        run.fatigue ??= {};
        if (world.alive[i] === 1 || world.exited[i] === 1) run.fatigue[world.uid[i]] = world.fatigue[i];
        else delete run.fatigue[world.uid[i]];
      }
      if (world.exited[i] === 1) exited.add(world.uid[i]);
    } else if (world.role[i] === Role.Companion) {
      const spec = world.specs[world.specIdx[i]];
      // 분대는 명부의 제 자리로 센다. 장수 곁의 동료는 전장에서 0번 분대에 서지만(`escortSquad`) 착장·소실은
      // 제 분대 자리(`loadouts[제 자리]`)의 것이다 — 전장 분대로 세면 장수의 착장을 굴렸다.
      const home = run.companions.indexOf(spec.troop);
      standing.push({ squad: home > 0 ? home : world.squad[i], id: spec.troop });
      const escort = run.escort === spec.troop;
      const isDown = world.downed[i] === 1;
      const add = isDown || run.raid ? 0 : heroDeedXp(world.killPower[i], world.hitsDealt[i], world.hitsTaken[i]);
      companionDeeds.push({ id: spec.troop, name: spec.name, kills: world.killCount[i], escort, deedXp: add, downed: isDown, squad: escort ? 0 : world.squad[i] });
      if (isDown) downed.push(spec.troop);
      else if (!run.raid) run.pendingMateDeedXp[spec.troop] = (run.pendingMateDeedXp[spec.troop] ?? 0) + add;
    } else if (world.role[i] === Role.Leader && !run.raid) {
      leaderDeedXp = heroDeedXp(world.killPower[i], world.hitsDealt[i], world.hitsTaken[i]);
    }
  }
  // 분대 색인 오름차순. 이 순서가 곧 소실 난수의 수열이다.
  standing.sort((a, b) => a.squad - b.squad);
  // 이 판을 쉰 병사(좁은 협곡에서 데려가지 않은 분대). 전장에 없었으므로 죽지도 흩어지지도 않고, 생환에 든다 — 빼면
  // 협곡을 넘을 때마다 쉰 분대만큼 생환 배율이 깎였다.
  const sat = run.squads.flat().filter((uid) => !killsOf.has(uid)).length;

  // 토벌은 두령을 베면 이긴 것이다. 흩어진 도적을 끝까지 쫓을 필요가 없다.
  const won = leaderAlive && (run.raid ? !world.chiefAlive() : enemyLeft === 0);

  // 장수가 쓰러지면 남은 병력이 후퇴한다(`World.leaderFall`). 아래 가장자리로 빠져나간 병사는 살아 돌아오고, 후퇴 시간
  // 안에 빠져나가지 못한 병사는 잃는다. 예전에는 장수가 쓰러지는 틱에 판이 끝나 데려간 병력을 전부 잃었다 — 플레이한
  // 사람이 청했다: "후퇴 절차를 거쳐줘. 무사히 하단으로 빠져나온 병사들은 살아돌아오게해줘."
  const wipe = !leaderAlive;
  const stranded = new Set([...alive].filter((uid) => !exited.has(uid)));
  // 부상(`WOUNDED_PCT`): 이긴 판에서 쓰러진 병사의 일부는 죽지 않는다. 계정에는 남고 출정 명단에서는 빠진다.
  const hurt = new Set<number>();
  if (won && !run.raid) {
    const seed = settleSeed(world);
    // 용사는 더 자주 돌아온다(`ELITE.woundedPct`). 그 판에 용사의 몸으로 선 병사만이다(`eliteInBattle`).
    for (const uid of dead) if (woundedRoll(seed, uid, elites.has(uid) ? ELITE.woundedPct : WOUNDED_PCT)) hurt.add(uid);
  }
  const gone = hurt.size > 0 ? new Set([...dead].filter((uid) => !hurt.has(uid))) : dead;
  const lost = wipe ? new Set([...dead, ...stranded]) : gone;

  // 잃은 병사를 지우기 전에 받아 둔다. 결과 화면이 "누가" 쓰러졌는지를 보인다. 경험치를
  // 주기 전이라 레벨은 나설 때의 것이다.
  const who = (s: Soldier): Who => ({ name: s.name, troop: s.troop, level: soldierLevel(s) });
  const menOf = (set: ReadonlySet<number>): Who[] =>
    army.soldiers
      .filter((s) => set.has(s.uid))
      .sort((a, b) => a.uid - b.uid)
      .map(who);
  const fallenMen = menOf(gone);
  const woundedMen = menOf(hurt);
  const scatteredMen = wipe ? menOf(stranded) : [];
  // 이름을 지우기 전에 전공을 뜬다. 많이 벤 순, 같으면 먼저 뽑힌 순(늘 같은 순서).
  // 오른 레벨 수(`levelGain`)는 경험치를 준 뒤에야 알므로 아래에서 채운다.
  const deedDraft = army.soldiers
    .filter((s) => killsOf.has(s.uid))
    .sort((a, b) => killsOf.get(b.uid)! - killsOf.get(a.uid)! || a.uid - b.uid)
    .map((s) => ({
      uid: s.uid,
      ...who(s),
      kills: killsOf.get(s.uid)!,
      bonusXp: !wipe && alive.has(s.uid) ? (deedOf.get(s.uid) ?? 0) : 0,
      fate: (hurt.has(s.uid) ? 'hurt' : dead.has(s.uid) ? 'fell' : wipe && stranded.has(s.uid) ? 'lost' : 'alive') as Deed['fate'],
    }));
  const leaderKills = world.leaderIdx >= 0 ? world.killCount[world.leaderIdx] : 0;
  army.soldiers = army.soldiers.filter((s) => !lost.has(s.uid));
  army.fallen += lost.size;

  const xpEach = battleXp(run.depth);
  const levelUps: LevelUp[] = [];
  // 병사마다 이번 판에 오른 레벨 수. 전공 순위가 경험치 대신 적는다(`Deed.levelGain`).
  const gainOf = new Map<number, number>();
  const toOf = new Map<number, number>();
  if (!wipe) {
    // uid 오름차순으로 돈다. `alive`는 Set이라 순서가 전장 인덱스를 따르는데, 화면에
    // 보이는 순서가 그런 우연에 기대면 안 된다.
    for (const uid of [...alive].sort((a, b) => a - b)) {
      const s = soldierById(army, uid);
      if (!s) continue;
      const before = soldierLevel(s);
      const xpBefore = s.xp;
      s.xp += xpEach + (deedOf.get(uid) ?? 0);
      gainOf.set(uid, levelsGained(xpBefore, s.xp, levelXp));
      const after = soldierLevel(s);
      toOf.set(uid, after);
      if (after > before) {
        let hp = 0;
        let atk = 0;
        for (let k = before + 1; k <= after; k++) {
          const g = growthRoll(s.uid, k);
          hp += g.hp;
          atk += g.atk;
        }
        const n = after - before;
        levelUps.push({ uid: s.uid, name: s.name, level: after, hp, atk, usualHp: n * SOLDIER_LEVEL_HP_PERMILLE, usualAtk: n * SOLDIER_LEVEL_ATK_PERMILLE });
      }
    }
  }

  const deeds: Deed[] = deedDraft.map((d) => ({
    ...d,
    levelGain: gainOf.get(d.uid) ?? 0,
    levelTo: toOf.get(d.uid) ?? d.level,
  }));

  // 두령을 베면 현상금이 붙는다. 졸개만 오래 잡는 쪽이 이득이 되면 안 된다.
  // 현상금은 고른 무리의 것이다(`RAID_BANDS`) — 가장 작은 무리는 예전 그대로(`RAID_BOUNTY`).
  const band = run.raid ? raidBand(run.raidBand) : 0;
  const bounty = won && run.raid ? RAID_BANDS[band].bounty : 0;
  // 큰 무리는 이기면 흩어지고, 출정에서 한 판 이기면 다시 모인다(`raidBandsOpen`).
  if (won && run.raid && band > 0) army.raidReady = false;
  // 전선의 판을 이겨야 다시 모인다(`frontFloor` — 월급의 자격과 같은 문턱). 얕은 곳의 한 판으로는 풀리지 않는다.
  // 최고 깊이는 이 뒤에 오른다(`firstClear`) — 새 깊이를 처음 이긴 판은 지금의 문턱보다 늘 깊다.
  if (won && !run.raid && !run.territory && run.depth >= frontFloor(army)) army.raidReady = true;
  // 토벌에서 물러난 판(`World.leaderFled`)은 **잡은 만큼** 받는다(노획만 — 현상금은 두령을 벤 판에만). 플레이한 사람이
  // 정했다(2026-10-09): "물러난 판의 보상: 잡은 만큼은 받게." 124에서는 내가 0냥으로 두었다(졸개만 잡고 빠져나오기를
  // 되풀이하는 길이 된다고 보았다 — 큰 무리는 이겨야 흩어지므로 그 되풀이에는 출정을 끼워 넣는 제한이 걸리지 않는다).
  // 그 길은 이제 열려 있다: 졸개 한 명의 노획은 10냥쯤이고 두령이 쫓아오므로 현상금에 견주면 작지만, 값은 재지 않았다.
  const gold = wipe ? 0 : battleGold(killScore) + bounty;
  // 출정의 노획은 쌓아 두었다가 철수할 때 들어온다(`RunState.pendingGold`). 한 판짜리(토벌·영토 공격)는 바로 들어온다.
  if (run.raid || run.territory) army.gold += gold;
  else run.pendingGold = (run.pendingGold ?? 0) + gold;

  // **살아 돌아온 동료가 큰다.** 업고 돌아온 판은 세지 않는다 — 쓰러진 것은
  // 함께 싸운 것이 아니다. 전멸한 판도 마찬가지다.
  if (!wipe) {
    for (const m of standing) {
      if (downed.includes(m.id)) continue;
      const st = army.companions.find((c) => c.id === m.id);
      if (st) st.battles++;
      if (!run.stood.includes(m.id)) run.stood.push(m.id);
    }
  }

  for (const id of downed) {
    const st = army.companions.find((c) => c.id === id);
    // 편성 화면은 증가 전의 sortieCount로 판단한다. 여기에 1을 더 얹으면
    // COMPANION_REST가 2인데 실제로는 세 번을 쉰다.
    if (st) st.readyAt = army.sortieCount + COMPANION_REST;
  }

  // 남은 병력으로 분대를 다시 짠다.
  // 다친 병사도 출정 명단에서 빠진다(계정에는 남는다) — 이 출정의 남은 판은 쉰다.
  run.squads = run.squads.map((list) => list.filter((uid) => !lost.has(uid) && !hurt.has(uid)));
  for (let sq = 0; sq < run.companions.length; sq++) {
    const id = run.companions[sq];
    if (id && downed.includes(id)) run.companions[sq] = null;
  }

  // 쓴 군수품을 하나씩 던다. 명부(`cfg.supplies`)에서 거른 것 — 서버(계정 대조)와 같은 값이다. 진 판에도 쓴 것은 쓴 것이다.
  const usedSupplies = battleSupplies(cfg);
  for (const id of usedSupplies) {
    const n = army.supplies[id] ?? 0;
    if (n > 1) army.supplies[id] = n - 1;
    else delete army.supplies[id];
  }
  // 척후: 앞의 몇 깊이를 안개 없이 본다(예보에만 쓴다).
  if (usedSupplies.includes(Supply.Scout)) run.scoutTo = Math.max(run.scoutTo ?? 0, run.depth + SCOUT_AHEAD);

  // 전투 밖의 주사위. 전투가 쓰는 수열과 겹치지 않게 따로 뽑는다.
  const settle = new Rng(settleSeed(world));

  // 전장을 차지해야 줍는다. 살아만 돌아온 판에서도 주우면 깊이 들어가는 위험이
  // 아니라 시간을 들이는 것이 전리품의 조건이 된다.
  //
  // 토벌에서는 줍지 않는다. 깊이 1이라 `dropChancePct`가 0을 돌려주지만,
  // 그 0에 기대면 깊이를 손대는 날 조용히 토벌에서도 떨어지기 시작한다.
  const drops: string[] = [];
  if (won && !run.raid) {
    // 관문을 넘으면 전리품이 반드시 하나 떨어진다(관문 보상, Todo5 5단계) — 첫 굴림이 반드시 맞는다.
    // 그 판의 특성이 부르는 칸이 더 잘 나온다(`TRAIT_LOOT`). 굴림은 늘 `DROP_ROLLS`번이다(빗나가도 주사위 수는 같다 —
    // 뒤따르는 소실 판정의 수열이 흔들리지 않게).
    for (let k = 0; k < DROP_ROLLS; k++) {
      const found = rollDrop(settle, run.depth, army.culture, k === 0 && isGate(run.depth), battleTraits(cfg));
      if (!found) continue;
      drops.push(found.id);
      addToStash(army, found.id);
    }
  }
  const drop: string | null = drops[0] ?? null;

  // 전멸하면 착용품이 사라진다. 장수부터 굴리고 동료는 분대 색인 오름차순이다.
  // 이 순서가 곧 난수 수열이라 바꾸면 서버 재현이 어긋난다.
  //
  // 주사위는 계정의 지금 착장이 아니라 **명부에 실린 착장**을 굴린다. 서버가
  // 가진 것은 리플레이의 cfg뿐이라, 계정 쪽을 굴리면 서버는 같은 판정을 다시
  // 해볼 방법이 없다.
  //
  // 토벌에서 지면 장비를 잃지 않는다. 소실은 부대를 걸고 깊이 들어간 대가인데,
  // 토벌은 병사를 한 명도 걸지 않는 싸움이다. 바닥에서 일어서라고 열어 준 길이
  // 가진 것마저 뺏으면 길이 아니라 함정이다.
  const gearLost: GearLoss[] = [];
  if (wipe && !run.raid) {
    const worn = cfg.loadouts ?? [];
    const wearers: { who: string; squad: number }[] = [
      { who: HERO, squad: 0 },
      ...standing.map((m) => ({ who: m.id, squad: m.squad })),
    ];
    for (const w of wearers) {
      const slots = rollWipeLoss(settle, worn[w.squad] ?? {});
      if (slots.length === 0) continue;
      // 강화한 물건은 ★ 하나만 깎인다(`wearDown`) — 실패의 무게(사용자와 정한 기준).
      const hit = wearDown(army, w.who, slots);
      const lost = hit.filter((h) => h.to === undefined);
      const down = hit.filter((h): h is { slot: Slot; from: string; to: string } => h.to !== undefined);
      if (hit.length > 0) {
        gearLost.push({ who: w.who, slots: lost.map((h) => h.slot), items: lost.map((h) => h.from), ...(down.length > 0 ? { worn: down } : {}) });
      }
    }
  }

  const survivors = (wipe ? exited.size : alive.size) + sat;
  // 토벌은 전과를 남기지 않는다. 병사를 걸지 않은 싸움이기 때문이다. 대신 장수가 벤 격만큼
  // 장수 경험치가 쌓인다 — 레벨에는 한도까지만 더해진다(`RAID_XP_CAP`). 쓰러지면 없다.
  // 한도에 닿은 뒤로도 적어 둔다. 한도를 바꾸는 날 이미 싸운 몫이 사라지면 안 된다.
  // 물러난 판도 벤 만큼 쌓인다(번 돈과 같다, 125) — 쓰러지면 없다. 레벨에 드는 몫에는 한도가 있다(`RAID_XP_CAP`).
  const raidXp = run.raid && leaderAlive ? killScore : 0;
  let battleMerit = 0;
  army.raidXp += raidXp;
  if (!run.raid) {
    if (leaderAlive) run.pendingDeedXp += leaderDeedXp;
    battleMerit = meritScore({
      killScore,
      deployed: run.deployed,
      survivors,
      leaderAlive,
      depth: run.depth,
      // 전장 특성의 가산(traits.ts). 명부(`cfg.traits`)에서 거른 것을 쓴다 — 서버(`summarize`)와 같은 값.
      bonusPct: traitMeritPct(battleTraits(cfg)),
    });
    run.pendingMerit += battleMerit;
  }
  // **사람은 전장을 쓸어야 얻는다.**
  //
  // 이긴 판에서만, 그리고 토벌에서는 오지 않는다. 토벌은 깊이가 늘 1이라
  // `companionAtDepth`가 null을 돌려주지만 그 null에 기대지 않는다 — 깊이를
  // 손대는 날 조용히 토벌에서도 사람이 붙기 시작한다.
  //
  // 철수를 기다리지 않고 바로 합류시킨다. 전과와 달리 사람은 걸어 나온 것이지
  // 들고 나온 것이 아니다. 여기서 전멸해도 만난 것은 만난 것이다.
  //
  // 누가 오는지는 깊이가 아니라 **이미 만난 수**가 정한다. 깊이를 색인으로 쓰면
  // 2를 건너뛰고 4를 쓴 사람에게 용장 없이 지장이 온다.
  // 이겨 본 가장 깊은 깊이. 출발 깊이 지름길이 여기서 열린다(`startDepths`). 처음 깬 전장인지는 갱신하기 전에 잰다 —
  // 결과 화면이 알린다(`BattleOutcome.firstClear`).
  // 영토 공격은 깊이를 깬 것이 아니다 — 수비 세기를 깊이로 적었을 뿐이다. 지름길을 열지 않는다.
  const firstClear = won && !run.raid && !run.territory && run.depth > army.deepest;
  if (firstClear) army.deepest = run.depth;

  // 이 출정에서 이긴 가장 깊은 깊이(`RunState.wonDepth`) — 월급의 자격이다.
  if (won && !run.raid && !run.territory && run.depth > (run.wonDepth ?? 0)) run.wonDepth = run.depth;

  let joined: string | null = null;
  if (won && !run.raid && !run.territory) {
    const met = companionAtDepth(army.culture, run.depth, army.companions.length);
    if (met && joinCompanion(army, met.id)) joined = met.id;
  }

  run.tally.gold += gold;
  run.tally.lost += lost.size;
  run.tally.hurt = (run.tally.hurt ?? 0) + hurt.size;
  for (const id of drops) run.tally.loot.push(id);
  if (joined) run.tally.joined.push(joined);

  // 영토 공격: 이기면 그 땅을 빼앗는다. 이기든 지든 세계가 한 차례 돈다(Todo9 — 시간은 실제 시간이 아니라 차례다).
  // 장수가 쓰러지면 진 것이다(`won`은 장수가 살아야 참).
  let captured: string | null = null;
  let gift: string | null = null;
  if (run.territory) {
    const realm = realmOf(army);
    if (won && realm.owner[run.territory.id] === run.territory.owner) {
      realm.owner[run.territory.id] = army.culture;
      // 막 빼앗은 땅은 잠시 되찾으러 오지 않는다(`GRACE_TURNS`, 6단계). 적는 것은 이 판이 곧 돌리는 차례(`realmTick`)다.
      (realm.heldSince ??= {})[run.territory.id] = realm.turn + 1;
      captured = run.territory.id;
      // 빼앗은 판의 특산 하나(곧바로 창고로). 소식에도 적는다 — 차례가 곧 돌아 이 판의 차례 번호다.
      gift = captureGift(army, realm, run.territory.id);
      if (gift) pushLog(realm, { turn: realm.turn + 1, kind: 'gift', territory: run.territory.id, item: gift, by: army.culture, general: '', dead: 0, hurt: 0, bounty: 0 });
    }
    realmTick(army);
  }

  run.battles++;
  // 토벌은 한 판으로 끝난다. 더 들어갈 깊이가 없다.
  if (run.raid) run.over = true;
  if (wipe) {
    // 출정이 끝났다 — 세계가 한 차례 돈다(영토 공격은 위에서, 토벌은 돌리지 않는다).
    if (!run.raid && !run.territory && !run.over) realmTick(army);
    run.over = true;
    run.failed = true;
    run.pendingMerit = 0;
    run.pendingDeedXp = 0;
    run.pendingMateDeedXp = {};
  }
  // 장수가 쓰러지면 쌓아 둔 노획이 사라진다(`RunState.pendingGold`). 결과 화면이 얼마를 잃었는지 적는다.
  const goldLost = wipe ? (run.pendingGold ?? 0) : 0;
  if (wipe) run.pendingGold = 0;

  return {
    won,
    leaderAlive,
    killScore,
    fallen: lost.size,
    fallenMen,
    woundedMen,
    scatteredMen,
    survivors,
    joined,
    gold,
    xpEach: wipe ? 0 : xpEach,
    levelUps,
    deeds,
    leaderKills,
    companionDeeds: companionDeeds
      .sort((a, b) => a.squad - b.squad)
      .map(({ id, name, kills, escort, deedXp, downed: d }) => ({ id, name, kills, escort, deedXp: wipe ? 0 : deedXp, downed: d })),
    battleMerit: wipe ? 0 : battleMerit,
    downedCompanions: downed,
    drop,
    drops,
    usedSupplies,
    gearLost,
    meritIfRetreat: run.pendingMerit,
    goldIfRetreat: run.pendingGold ?? 0,
    goldLost,
    raidXp,
    leaderDeedXp: wipe ? 0 : leaderDeedXp,
    deedXpIfRetreat: run.pendingDeedXp,
    firstClear,
    captured,
    gift,
  };
}

export function advance(run: RunState): void {
  if (run.over) return;
  // 깊이의 울타리(`MAX_DEPTH` 999) 너머로는 가지 않는다. 깊이는 무한이지만 리플레이·주소창의 깊이를 정수로 막는 자리다 —
  // 머릿수는 30 너머에서 멈추므로(`HEAD_DEPTH`) 전장 정원 때문은 아니다.
  if (run.depth >= MAX_DEPTH) return;
  run.depth++;
}

/**
 * 갈림길에서 물러선다(사용자가 청했다: "취소 버튼도 달아줘. 이전 화면(편성 혹은 결과)으로 돌아가는 버튼").
 * - 아직 한 판도 싸우지 않았으면(출정 첫 판) 출정을 거둔다: 낸 급료를 돌려주고 출정 횟수를 되돌린다. 출정 시드
 *   (`Army.nextSeed`)는 첫 판을 정산할 때 비우므로 그대로 남는다 — 물렀다 다시 나서도 같은 땅이라 공짜로 다시 굴릴 수 없다.
 *   'start'를 돌려주고, 부르는 쪽이 출정을 버리고 편성 화면을 연다.
 * - 싸운 뒤(진군해서 왔으면) 진군을 무른다: 깊이를 하나 내린다. 'result'를 돌려주고, 부르는 쪽이 그 판의 결과를 다시 연다.
 * 갈림길에 서 있지 않으면(이미 길을 골랐으면) 아무것도 하지 않고 null.
 */
export function backFromCrossroads(army: Army, run: RunState): 'start' | 'result' | null {
  if (run.over || !needsRoute(run)) return null;
  if (run.battles === 0) {
    army.gold += run.wage ?? 0;
    army.sortieCount = Math.max(0, army.sortieCount - 1);
    run.wage = 0;
    return 'start';
  }
  if (run.depth <= 1) return null;
  run.depth--;
  return 'result';
}

/** 더 들어갈 곳이 있는가. 결과 화면이 "진군"을 막는 데 쓴다. */
export function canAdvance(run: RunState): boolean {
  // 영토 공격은 한 판으로 끝난다.
  return !run.over && !run.territory && run.depth < MAX_DEPTH;
}

/** 철수. 전과가 확정되고 살아남은 병력이 계정으로 돌아온다. */
/**
 * 월급을 받는 출정의 깊이 폭. 이 출정에서 이긴 가장 깊은 깊이가 계정의 최고 깊이(`Army.deepest`)에서 이 칸 안이어야
 * 한다 — 지름길 두 구간이다.
 */
export const SALARY_REACH = FRONT_REACH;

/** 월급을 받으려면 이 깊이 이상에서 이긴 판이 있어야 한다(`salaryDue`). 화면이 까닭을 적을 때 쓴다. */
export function salaryFloor(army: Army): number {
  return frontFloor(army);
}

/**
 * 지금 철수하면 받을 월급(냥). 결과 화면의 예고도 이 함수를 쓴다(`retreat`과 갈리지 않게).
 *
 * - 출정에만 있다. 토벌과 영토 공격에는 없다 — 한 판짜리를 되풀이해 월급을 버는 길이 된다.
 * - **전선에서 이긴 출정에만 있다**: 이 출정에서 **이긴** 가장 깊은 깊이(`RunState.wonDepth`)가 최고 깊이에서
 *   `SALARY_REACH` 안이어야 한다(`salaryFloor`). 이 조건이 없으면 다 키운 부대로 깊이 1을 한 판 이기고 돌아오기를
 *   되풀이하는 것이 위험 없는 돈벌이가 된다. 처음 하는 계정은 최고 깊이가 얕아 한 판만 이기면 이 문턱은 넘는다.
 *   **얕은 곳의 반복을 모두 막는 것은 아니다**: 깊이 5까지는 월급이 없고, 자격 밖의 얕은 승리로 제 관등의 온 몫을 받지
 *   못하게 할 뿐이다. 다 키운 계정이 깊이 6·11을 이기고 돌아오면 300·800냥은 받는다 — 실제로 한 판을 이겨야 하고
 *   그 계정의 전선 출정(노획 수천 냥)에 견주면 작다. 그 효율은 경제 묶음에서 잰다(리뷰가 권했다).
 *   처음에는 지금 깊이(`run.depth`)를 봤다 — "철수는 이긴 판 뒤에만 한다"고 적었는데 틀렸다. 병사가 모두 빠져나가면
 *   장수가 살아 있는 채로 진 판도 끝나고, 그 결과 화면에서도 철수한다. 상장군이 병사 한 명을 데리고 깊이 21에 나가 첫
 *   틱에 후퇴하면 2.5초 만에 피해 0으로 월급 2,500냥을 받았다(급료 4냥 — 리뷰가 재현했다). 이긴 판을 적어 그것으로 가른다.
 * - **관등의 월급에는 깊이가 걸려 있다**(`SALARY_DEPTH`, `salaryAt`): 이긴 깊이가 제 관등의 깊이보다 얕으면 그 깊이로
 *   받을 수 있는 관등의 월급을 받는다. 위의 조건은 최고 깊이를 따르는데, 관등은 영토전으로도 오르고 최고 깊이는 출정으로만
 *   오른다 — 최고 깊이가 얕은 고관등 계정이 깊이 1에서 상장군의 월급을 받았다(리뷰가 재현했다).
 * - 관등은 이 출정의 전과를 더한 값으로 본다 — 이번 출정으로 오른 관등의 월급을 바로 받는다.
 * - 장수가 쓰러진 출정에는 없다(`retreat`이 불리지 않는다 — 전과·노획과 같다).
 */
export function salaryDue(army: Army, run: RunState): number {
  if (run.over || run.raid || run.territory) return 0;
  if (run.wonDepth === undefined || run.wonDepth < salaryFloor(army)) return 0;
  // 관등마다 깊이가 걸려 있다(`SALARY_DEPTH`) — 얕은 곳에서 이겼으면 그 깊이의 몫만 받는다.
  return salaryAt(army.totalMerit + run.pendingMerit, run.wonDepth);
}

/**
 * **월급의 조건표** — 화면이 플레이어에게 알리는 데 쓴다(`salaryDue`와 같은 규칙을 글로 풀 재료). 지금 관등에서, 출정에서
 * 이긴 가장 깊은 깊이가 `from` 이상이면 `pay`냥이다. 깊은 쪽부터 적고, 맨 끝 줄의 `from`보다 얕으면 없다. 관등에 월급이
 * 없으면(편장) 빈 표다.
 *
 * 최고 깊이의 문턱(`salaryFloor`)과 관등마다의 깊이(`SALARY_DEPTH`)를 합친 것이다: 문턱보다 얕은 관등의 깊이는 문턱으로
 * 올라가고, 그래서 `from`이 같아진 줄은 높은 관등의 것 하나만 남는다. 플레이한 사람이 물었다: "월급이 출정하고 한 판
 * 이기고 철수하면 항상 나오는 거야?" — 칩에 "철수 월급 N냥"만 적어 조건이 어디에도 없었다.
 */
export function salarySteps(army: Army): { from: number; pay: number }[] {
  const floor = salaryFloor(army);
  const out: { from: number; pay: number }[] = [];
  for (let k = RANKS.indexOf(rankFor(army.totalMerit)); k >= 1; k--) {
    const pay = RANK_SALARY[k] ?? 0;
    if (pay <= 0) continue;
    const from = Math.max(floor, SALARY_DEPTH[k] ?? 1);
    // 같은 깊이에서 받는 것은 가장 높은 관등의 몫이다(`salaryAt`이 위에서부터 본다).
    if (out.length > 0 && out[out.length - 1].from <= from) continue;
    out.push({ from, pay });
  }
  return out;
}

/**
 * 지금 철수하면 확정되는 것(화면이 돌아온 뒤에 적는다, `ui/homecoming.ts`). `retreat`을 부르기 전에 떠야 한다 — 부르면
 * 쌓아 둔 노획이 비워지고 전과가 계정에 더해져 관등이 달라진다.
 */
export function retreatReceipt(army: Army, run: RunState): {
  merit: number;
  loot: number;
  salary: number;
  fullSalary: number;
  needDepth: number;
  floorDepth: number;
  rank: string;
  raid: boolean;
} {
  // 관등은 이 출정의 전과를 더한 값으로 본다(`salaryDue`와 같다).
  const after = army.totalMerit + run.pendingMerit;
  return {
    merit: run.pendingMerit,
    loot: run.pendingGold ?? 0,
    salary: salaryDue(army, run),
    fullSalary: salaryFor(after),
    needDepth: Math.max(salaryFloor(army), salaryDepthFor(after)),
    floorDepth: Math.max(salaryFloor(army), SALARY_DEPTH[1] ?? 1),
    rank: rankFor(after).name,
    raid: run.raid === true,
  };
}

export function retreat(army: Army, run: RunState): number {
  if (run.over) return 0;
  const merit = run.pendingMerit;
  // 월급. 전과를 더하기 전에 잰다(`salaryDue`가 쌓인 전과를 더해 관등을 본다).
  army.gold += salaryDue(army, run);
  // 쌓아 둔 노획이 이제 들어온다(`RunState.pendingGold`).
  army.gold += run.pendingGold ?? 0;
  run.pendingGold = 0;
  army.totalMerit += merit;
  army.deedXp += run.pendingDeedXp;
  // 함께 선 동료도 같은 전과를 경험치로 받는다(장수와 같은 길 — `companionCaptain`). 제 전투 성과 몫도 함께.
  for (const st of earningCompanions(army, run)) st.xp += merit + (run.pendingMateDeedXp[st.id] ?? 0);
  // 최고 전과(순위)는 출정만 — 영토 공격은 한 판짜리라 깊이를 건 출정과 견줄 것이 아니다. 관등·성장에는 든다.
  if (!run.territory && merit > army.bestMerit) army.bestMerit = merit;
  // 출정이 끝났다 — 세계가 한 차례 돈다(영토 공격은 판을 정산할 때 이미 돌았다).
  if (!run.territory && !run.raid) realmTick(army);
  run.over = true;
  return merit;
}

/**
 * 지금 철수하면 전과를 경험치로 받을 동료. 한 판이라도 전장에 서서 살아 남았고(`stood`), 그 뒤 쓰러지지 않아
 * 아직 명단(`companions`)에 남은 이다 — 쓰러진 동료는 판 도중 명단에서 빠진다. 업고 돌아온 것은 함께 싸운 것이
 * 아니다. 명단에 있어도 전장에 서지 않는 경우(모르는 id, 두 분대에 적힌 같은 id의 뒤쪽)도 받지 않는다. 결과
 * 화면의 예고도 이 함수를 써야 실제와 갈리지 않는다.
 */
export function earningCompanions(army: Army, run: RunState): CompanionState[] {
  const out: CompanionState[] = [];
  // `stood`을 훑는다(중복이 없다). `companions`를 훑으면 같은 id가 두 자리에 든 조작된 선택이 두 번 받는다.
  for (const id of run.stood) {
    if (!run.companions.includes(id)) continue;
    const st = army.companions.find((c) => c.id === id);
    if (st) out.push(st);
  }
  return out;
}

/** 한 사람이 이번 판으로 오르는 레벨: 소수(`gain`)와 정수 레벨의 앞뒤(`from` → `to`). 못 받으면 셋 다 0. */
export interface LevelGain {
  readonly gain: number;
  readonly from: number;
  readonly to: number;
}

/**
 * 이번 판으로 장수·동료가 오르는 레벨(`LevelGain`) — 결과 화면의 전공 순위가 적는다. 동료는
 * `outcome.companionDeeds`와 같은 순서다. 경험치를 받기 전의 계정(`army`)과 판(`run`)에서 잰다.
 *
 * 장수·동료의 전장 몫은 철수해야 들어오고, 앞선 판에서 쌓아 둔 몫(`pendingMerit` 등) 위에 얹힌다. 그래서 "철수하면 될
 * 경험치"에서 이번 판 몫을 뺀 자리부터 잰다 — 지금 계정의 경험치에서 재면 앞선 판 몫이 이번 판 것처럼 부풀었다.
 * 받는 사람의 판단은 `retreat`과 같다(`earningCompanions`). 토벌 경험치는 이미 계정에 들었고, 한도(`RAID_XP_CAP`)를
 * 넘은 몫은 레벨에 들지 않는다.
 */
export function heroLevelGains(
  army: Army,
  run: RunState,
  outcome: BattleOutcome,
): { readonly leader: LevelGain; readonly companions: readonly LevelGain[] } {
  const none: LevelGain = { gain: 0, from: 0, to: 0 };
  const span = (before: number, after: number): LevelGain => ({
    gain: levelsGained(before, after, heroLevelMerit),
    from: heroLevelFor(before),
    to: heroLevelFor(after),
  });
  const gainTo = (after: number, xp: number): LevelGain => (xp > 0 ? span(Math.max(0, after - xp), after) : none);
  const heroXp = heroXpOf(army);
  const leader = run.raid
    ? span(army.totalMerit + Math.min(army.raidXp - outcome.raidXp, RAID_XP_CAP) + army.deedXp, heroXp)
    : outcome.leaderAlive && !run.failed
      ? gainTo(heroXp + outcome.meritIfRetreat + outcome.deedXpIfRetreat, outcome.battleMerit + outcome.leaderDeedXp)
      : none;
  const earning = run.raid || run.failed ? [] : earningCompanions(army, run);
  const companions = outcome.companionDeeds.map((c) => {
    const st = earning.find((s) => s.id === c.id);
    return st && !c.downed
      ? gainTo(st.xp + outcome.meritIfRetreat + (run.pendingMateDeedXp[st.id] ?? 0), outcome.battleMerit + c.deedXp)
      : none;
  });
  return { leader, companions };
}

export function remainingTroops(run: RunState): number {
  return run.squads.reduce((n, list) => n + list.length, 0);
}

/** 지금 상태의 생환 배율. 화면이 위험을 보여주는 데 쓴다. */
export function currentSurvivalPct(run: RunState): number {
  return survivalPct(run.deployed, remainingTroops(run));
}
